/**
 * Enum utility functions for dev-tools
 * Helper functions to improve enum display and handling
 */
import { getEnumValuesDynamic } from "./dynamic-enum-extractor";

export interface EnumValue {
  name: string;
  value: number;
  description?: string;
}

/**
 * Converts enum name from SCREAMING_SNAKE_CASE to Title Case
 */
export const formatEnumName = (enumName: string): string => {
  return enumName
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Gets a human-readable display text for an enum value
 */
export const getEnumDisplayText = (enumValue: EnumValue): string => {
  // Special cases for common enum patterns
  if (enumValue.name === "UNSPECIFIED" || enumValue.name === "UNKNOWN") {
    return "Unspecified";
  }

  if (enumValue.name === "NONE" || enumValue.name === "NULL") {
    return "None";
  }

  // Convert to title case
  return formatEnumName(enumValue.name);
};

/**
 * Attempts to detect if a field might be an enum based on common naming patterns
 * This is used by the dynamic enum extractor
 */
export const detectEnumField = (fieldName: string): string | null => {
  const lowerFieldName = fieldName.toLowerCase();

  // Direct matches
  if (lowerFieldName === "chargifystate" || lowerFieldName === "state") {
    return "chargifyState";
  }
  if (
    lowerFieldName === "paymentmethod" ||
    lowerFieldName === "payment_method"
  ) {
    return "paymentMethod";
  }
  if (lowerFieldName === "customertype" || lowerFieldName === "customer_type") {
    return "customerType";
  }
  if (
    lowerFieldName === "subscriptiontype" ||
    lowerFieldName === "subscription_type" ||
    lowerFieldName === "type"
  ) {
    return "subscriptionType";
  }
  if (lowerFieldName === "roletype" || lowerFieldName === "role_type") {
    return "roleType";
  }
  if (lowerFieldName === "currency") {
    return "currency";
  }
  if (lowerFieldName === "product") {
    return "product";
  }
  if (lowerFieldName === "period") {
    return "period";
  }
  if (lowerFieldName === "chargifysite" || lowerFieldName === "site") {
    return "chargifySite";
  }
  if (lowerFieldName === "tier") {
    return "tier";
  }
  if (
    lowerFieldName === "seattype" ||
    lowerFieldName === "seat_type" ||
    lowerFieldName === "subscriptionseattype" ||
    lowerFieldName === "subscription_seat_type"
  ) {
    return "subscriptionSeatTypes";
  }

  // Pattern-based detection
  if (lowerFieldName.includes("state") || lowerFieldName.endsWith("state")) {
    return "chargifyState";
  }
  if (
    lowerFieldName.includes("payment") &&
    (lowerFieldName.includes("method") || lowerFieldName.endsWith("method"))
  ) {
    return "paymentMethod";
  }
  if (lowerFieldName.includes("type") && lowerFieldName.includes("customer")) {
    return "customerType";
  }
  if (
    lowerFieldName.includes("type") &&
    (lowerFieldName.includes("subscription") || lowerFieldName.includes("sub"))
  ) {
    return "subscriptionType";
  }
  if (lowerFieldName.includes("type") && lowerFieldName.includes("role")) {
    return "roleType";
  }
  if (
    lowerFieldName.includes("currency") ||
    lowerFieldName.endsWith("currency")
  ) {
    return "currency";
  }
  if (lowerFieldName.includes("period") || lowerFieldName.endsWith("period")) {
    return "period";
  }
  if (lowerFieldName.includes("tier") || lowerFieldName.endsWith("tier")) {
    return "tier";
  }
  if (
    lowerFieldName.includes("site") &&
    (lowerFieldName.includes("chargify") || lowerFieldName.includes("billing"))
  ) {
    return "chargifySite";
  }
  if (
    (lowerFieldName.includes("seat") && lowerFieldName.includes("type")) ||
    lowerFieldName.endsWith("seattype") ||
    lowerFieldName === "seattype" ||
    lowerFieldName === "seat_type"
  ) {
    return "subscriptionSeatTypes";
  }

  return null;
};

/**
 * Gets the enum string representation for a numerical value using dynamic extraction
 */
export const getEnumStringValue = (
  fieldName: string,
  numericValue: number
): string | null => {
  const dynamicValues = getEnumValuesDynamic(fieldName);
  if (!dynamicValues) return null;

  const enumValue = dynamicValues.find((e) => e.value === numericValue);
  return enumValue ? enumValue.name : null;
};

/**
 * Formats an enum value for display in responses using dynamic extraction
 */
export const formatEnumForDisplay = (
  fieldName: string,
  numericValue: number
): { numeric: number; stringValue: string | null; displayText: string } => {
  const stringValue = getEnumStringValue(fieldName, numericValue);

  if (stringValue) {
    const displayText = formatEnumName(stringValue);
    return {
      numeric: numericValue,
      stringValue,
      displayText,
    };
  }

  return {
    numeric: numericValue,
    stringValue: null,
    displayText: numericValue.toString(),
  };
};

/**
 * Gets enum values for a field, using dynamic extraction with minimal fallback
 */
export const getEnumValuesForField = (
  fieldName: string,
  providedEnumValues?: EnumValue[]
): EnumValue[] => {
  // Check if provided enum values are valid (not just numeric strings)
  const hasValidProvidedValues =
    providedEnumValues &&
    providedEnumValues.length > 0 &&
    providedEnumValues.some(
      (enumVal) =>
        enumVal.name &&
        enumVal.name !== String(enumVal.value) && // Name should not be just the numeric value as string
        enumVal.name.length > 1 && // Name should be more than just a single character
        isNaN(Number(enumVal.name)) // Name should not be a number
    );

  if (hasValidProvidedValues) {
    return providedEnumValues;
  }

  // Try dynamic protobuf enum extraction
  const dynamicValues = getEnumValuesDynamic(fieldName);
  if (dynamicValues && dynamicValues.length > 0) {
    return dynamicValues;
  }

  // Minimal fallback for unknown fields
  return [];
};

/**
 * Sorts enum values for better display order
 * Places common values like UNSPECIFIED, NONE first, then sorts alphabetically
 */
export const sortEnumValues = (enumValues: EnumValue[]): EnumValue[] => {
  return [...enumValues].sort((a, b) => {
    // Priority order for common enum values
    const priority = ["UNSPECIFIED", "UNKNOWN", "NONE", "NULL", "DEFAULT"];

    const aPriority = priority.indexOf(a.name);
    const bPriority = priority.indexOf(b.name);

    if (aPriority !== -1 && bPriority !== -1) {
      return aPriority - bPriority;
    }

    if (aPriority !== -1) return -1;
    if (bPriority !== -1) return 1;

    // Sort alphabetically by display text
    const aDisplay = getEnumDisplayText(a);
    const bDisplay = getEnumDisplayText(b);

    return aDisplay.localeCompare(bDisplay);
  });
};
